import { Component, OnDestroy } from '@angular/core';
import { Observable, Subject, isObservable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IFloatingFilter, IFloatingFilterParams } from 'ag-grid-community';

export type TextFilterModel = {
  filterType: string;
  type: string;
  filter: string;
};

export interface DropdownFloatingFilterParams extends IFloatingFilterParams {
  dropdownOptions?: string[] | Observable<string[]>;
  filterOptions?: string[] | Observable<string[]>;
  defaultOption: string;
  onFloatingFilterChanged?(model: TextFilterModel | null): void;
}

@Component({
  template: `
    <select
      class="form-control"
      [(ngModel)]="currentValue"
      (ngModelChange)="valueChanged()"
    >
      <option value="">{{ params?.defaultOption }}</option>
      <option
        *ngFor="let option of options"
        [value]="option"
        [textContent]="option"
      ></option>
    </select>
  `,
  standalone: false
})
export class DropdownFloatingFilterComponent
  implements IFloatingFilter<DropdownFloatingFilterParams>, OnDestroy
{
  public params: DropdownFloatingFilterParams;
  public currentValue = '';
  public options: string[] = [];
  private readonly destroy$ = new Subject<void>();

  public agInit(params: DropdownFloatingFilterParams): void {
    this.params = params;
    const optionsSource = params?.dropdownOptions ?? params?.filterOptions;

    if (isObservable(optionsSource)) {
      optionsSource
        .pipe(takeUntil(this.destroy$))
        .subscribe((filterOptions: string[]) => {
          this.options = filterOptions || [];
        });
    } else if (Array.isArray(optionsSource)) {
      this.options = optionsSource;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public valueChanged(): void {
    const model = this.buildModel();
    if (this.params.parentFilterInstance) {
      this.params.parentFilterInstance((instance: any) => {
        const res = instance.setModel(model);
        if (res && typeof res.then === 'function') {
          res.then(() => {
            this.params.api?.onFilterChanged();
          });
        } else {
          this.params.api?.onFilterChanged();
        }
      });
    } else if (this.params.api) {
      // Fallback for AG Grid contexts where parentFilterInstance is not provided.
      const colId = this.params.column?.getColId?.();
      if (!colId) {
        return;
      }

      const currentModel = this.params.api.getFilterModel?.() || {};
      const nextModel = { ...currentModel } as Record<string, unknown>;

      if (model) {
        nextModel[colId] = model;
      } else {
        delete nextModel[colId];
      }

      this.params.api.setFilterModel?.(nextModel);
      this.params.api.onFilterChanged?.();
    } else if (typeof this.params.onFloatingFilterChanged === 'function') {
      this.params.onFloatingFilterChanged(model);
    }
  }

  public onParentModelChanged(parentModel: TextFilterModel | null): void {
    if (!parentModel) {
      this.currentValue = '';
    } else {
      this.currentValue = parentModel.filter;
    }
  }

  private buildModel(): TextFilterModel | null {
    if (this.currentValue === '') {
      return null;
    }

    return {
      filterType: 'agTextColumnFilter',
      type: 'equals',
      filter: this.currentValue
    };
  }
}
